export type RationalString = string;

export interface EquationSide {
  x: RationalString;
  constant: RationalString;
}

export interface Equation {
  left: EquationSide;
  right: EquationSide;
}

export type AlgebraOperation = {
  kind: 'add' | 'subtract' | 'multiply' | 'divide';
  value: string;
};

export type EquationSolution =
  | { kind: 'unique'; value: RationalString }
  | { kind: 'none' }
  | { kind: 'all' };

interface Rational {
  n: bigint;
  d: bigint;
}
const MAX = 1_000_000_000_000n;
const fail = (message: string): never => {
  throw new Error(message);
};
const gcd = (a: bigint, b: bigint): bigint => {
  while (b) [a, b] = [b, a % b];
  return a < 0n ? -a : a;
};
const check = (numerator: bigint, denominator: bigint): Rational => {
  let n = numerator;
  let d = denominator;
  if (d === 0n) fail('A fraction cannot have a zero denominator.');
  if (d < 0n) [n, d] = [-n, -d];
  const divisor = gcd(n < 0n ? -n : n, d);
  n /= divisor;
  d /= divisor;
  if ((n < 0n ? -n : n) > MAX || d > MAX)
    fail("That value is outside this activity's safe range.");
  return { n, d };
};
const expandExponent = (source: string): string => {
  const match = source.match(/^([+-]?)(\d+)(?:\.(\d*))?[eE]([+-]?\d+)$/);
  if (!match) throw new Error(`Invalid rational number: ${source}`);
  if (Math.abs(Number(match[4])) > 100)
    fail(`Invalid rational number: ${source}`);
  const digits = match[2] + (match[3] || '');
  const point = match[2].length + Number(match[4]);
  if (point <= 0) return `${match[1]}0.${'0'.repeat(-point)}${digits}`;
  if (point >= digits.length)
    return `${match[1]}${digits}${'0'.repeat(point - digits.length)}`;
  return `${match[1]}${digits.slice(0, point)}.${digits.slice(point)}`;
};
const rational = (value: string | number): Rational => {
  const wasNumber = typeof value === 'number';
  if (wasNumber && !Number.isFinite(value))
    fail('A coefficient must be a finite number.');
  if (typeof value !== 'string' && typeof value !== 'number')
    fail('A coefficient must be a number or rational string.');
  let source = String(value).trim();
  if (wasNumber && /e/i.test(source)) source = expandExponent(source);
  if (source.length > 128)
    fail("That value is outside this activity's safe range.");
  const fractionMatch = source.match(/^([+-]?\d+)\/(\d+)$/);
  if (fractionMatch)
    return check(BigInt(fractionMatch[1]), BigInt(fractionMatch[2]));
  const decimalMatch = source.match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!decimalMatch) throw new Error(`Invalid rational number: ${value}`);
  const scale = 10n ** BigInt((decimalMatch[3] || '').length);
  const numerator =
    BigInt(decimalMatch[2] + (decimalMatch[3] || '')) *
    (decimalMatch[1] === '-' ? -1n : 1n);
  return check(numerator, scale);
};
const add = (a: Rational, b: Rational): Rational =>
  check(a.n * b.d + b.n * a.d, a.d * b.d);
const subtract = (a: Rational, b: Rational): Rational =>
  check(a.n * b.d - b.n * a.d, a.d * b.d);
const multiply = (a: Rational, b: Rational): Rational =>
  check(a.n * b.n, a.d * b.d);
const divide = (a: Rational, b: Rational): Rational =>
  b.n === 0n ? fail('Cannot divide by zero.') : check(a.n * b.d, a.d * b.n);
const zero = (): Rational => ({ n: 0n, d: 1n });
const text = (value: Rational): RationalString =>
  value.d === 1n ? String(value.n) : `${value.n}/${value.d}`;
const equation = (
  leftX: Rational,
  leftConstant: Rational,
  rightX: Rational,
  rightConstant: Rational,
): Equation => ({
  left: { x: text(leftX), constant: text(leftConstant) },
  right: { x: text(rightX), constant: text(rightConstant) },
});
const read = (input: Equation) => {
  if (!input?.left || !input?.right)
    fail('Equation must have left and right sides.');
  return {
    lx: rational(input.left.x),
    lc: rational(input.left.constant),
    rx: rational(input.right.x),
    rc: rational(input.right.constant),
  };
};

export const normalizeRational = (value: string | number): RationalString =>
  text(rational(value));
export const makeEquation = (
  a: string | number,
  b: string | number,
  c: string | number,
  d: string | number,
): Equation => equation(rational(a), rational(b), rational(c), rational(d));

export const formatSide = (
  x: string | number,
  constant: string | number,
): string => {
  const a = rational(x);
  const b = rational(constant);
  if (a.n === 0n) return text(b);
  const variable = a.n === a.d ? 'x' : a.n === -a.d ? '-x' : `${text(a)}x`;
  if (b.n === 0n) return variable;
  return `${variable} ${b.n < 0n ? '-' : '+'} ${text({ n: b.n < 0n ? -b.n : b.n, d: b.d })}`;
};

export const formatEquation = (input: Equation): string => {
  const value = read(input);
  return `${formatSide(text(value.lx), text(value.lc))} = ${formatSide(text(value.rx), text(value.rc))}`;
};

const operationValue = (
  value: string,
  allowVariable: boolean,
): { x: Rational; constant: Rational } => {
  if (typeof value !== 'string') fail('Operation value must be a string.');
  const source = value.trim();
  if (source.endsWith('x')) {
    if (!allowVariable)
      fail('Multiply and divide only accept constant values.');
    const coefficient = source.slice(0, -1);
    return {
      x: rational(
        coefficient === '' ? '1' : coefficient === '-' ? '-1' : coefficient,
      ),
      constant: zero(),
    };
  }
  return { x: zero(), constant: rational(source) };
};

export const normalizeOperation = (
  operation: AlgebraOperation,
): AlgebraOperation => {
  if (
    !operation ||
    !['add', 'subtract', 'multiply', 'divide'].includes(operation.kind)
  )
    fail('Choose add, subtract, multiply, or divide.');
  if (typeof operation.value !== 'string')
    fail('Operation value must be a string.');
  const source = operation.value.trim();
  if (source.endsWith('x')) {
    if (operation.kind === 'multiply' || operation.kind === 'divide')
      fail('Multiply and divide only accept constant values.');
    const coefficientSource = source.slice(0, -1);
    const coefficient = normalizeRational(
      coefficientSource === ''
        ? '1'
        : coefficientSource === '-'
          ? '-1'
          : coefficientSource,
    );
    return {
      kind: operation.kind,
      value:
        coefficient === '1'
          ? 'x'
          : coefficient === '-1'
            ? '-x'
            : `${coefficient}x`,
    };
  }
  return { kind: operation.kind, value: normalizeRational(source) };
};

export const applyOperation = (
  input: Equation,
  operation: AlgebraOperation,
): Equation => {
  const current = read(input);
  const normalizedOperation = normalizeOperation(operation);
  const canUseVariable =
    normalizedOperation.kind === 'add' ||
    normalizedOperation.kind === 'subtract';
  const value = operationValue(normalizedOperation.value, canUseVariable);
  if (canUseVariable) {
    const apply = normalizedOperation.kind === 'add' ? add : subtract;
    return equation(
      apply(current.lx, value.x),
      apply(current.lc, value.constant),
      apply(current.rx, value.x),
      apply(current.rc, value.constant),
    );
  }
  if (value.x.n !== 0n)
    fail('Multiply and divide only accept constant values.');
  if (value.constant.n === 0n)
    fail(
      `${normalizedOperation.kind === 'multiply' ? 'Multiplying' : 'Dividing'} by zero is not a reversible equation operation.`,
    );
  const apply = normalizedOperation.kind === 'multiply' ? multiply : divide;
  return equation(
    apply(current.lx, value.constant),
    apply(current.lc, value.constant),
    apply(current.rx, value.constant),
    apply(current.rc, value.constant),
  );
};

export const solve = (input: Equation): EquationSolution => {
  const current = read(input);
  const coefficient = subtract(current.lx, current.rx);
  const constant = subtract(current.rc, current.lc);
  if (coefficient.n === 0n)
    return constant.n === 0n ? { kind: 'all' } : { kind: 'none' };
  return { kind: 'unique', value: text(divide(constant, coefficient)) };
};

export const substitute = (input: Equation, value: string | number) => {
  const current = read(input);
  const x = rational(value);
  const left = add(multiply(current.lx, x), current.lc);
  const right = add(multiply(current.rx, x), current.rc);
  return {
    left: text(left),
    right: text(right),
    equal: left.n === right.n && left.d === right.d,
  };
};

export const isIsolated = (
  input: Equation,
  expected: string | number,
): boolean => {
  const normalized = normalizeRational(expected);
  return (
    (input.left.x === '1' &&
      input.left.constant === '0' &&
      input.right.x === '0' &&
      input.right.constant === normalized) ||
    (input.right.x === '1' &&
      input.right.constant === '0' &&
      input.left.x === '0' &&
      input.left.constant === normalized)
  );
};
